import { coacheeInitials } from '@efdigital/ll-addon-coaching';
import {
  formatDate,
  formatTimeRange,
} from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-dates';
import { getSessionStatusDisplay } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-display';
import type {
  CoachingConfig,
  CoachingSession,
  CoachProfile,
} from '@efdigital/ll-addon-data';
import type { CoachCoacheePii } from '@efdigital/ll-cdk-coaching-sessions-api-types';
import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';

import SessionNotes from '../session-notes.gts';
import { SessionActions } from './session-actions.gts';

interface SessionCardSignature {
  Args: {
    session: CoachingSession;
    coach: CoachProfile;
    coachee?: CoachCoacheePii;
    coachingConfig: CoachingConfig;
    onChanged?: () => void | Promise<void>;
  };
  Element: HTMLDivElement;
}

function isPast(session: CoachingSession): boolean {
  return new Date(session.startTime) < new Date();
}

function toDate(date: string): Date {
  return new Date(date);
}

function coacheeDisplayName(
  coachee: CoachCoacheePii | undefined,
  userId: string
): string {
  return coachee?.displayName || userId;
}

/**
 * Danger (canceled) uses explicit white type: `text-bg-danger` picks dark
 * text against this theme's coral fill.
 */
function statusBadgeClass(color: string): string {
  if (color === 'danger') {
    return 'bg-danger text-white';
  }

  return `text-bg-${color}`;
}

const SessionCard: TOC<SessionCardSignature> = <template>
  <div class="card border shadow-sm rounded-4 overflow-hidden" ...attributes>
    <div class="card-body p-3 px-md-4 py-md-3">
      {{! The avatar sits beside the session header only. Notes render as a sibling
          below, so they get the full card width instead of being squeezed into the
          column left over next to the avatar on narrow screens. }}
      <div class="d-flex flex-row align-items-start gap-3">
        <LinkTo
          @route="coaching.coach.coachees.coachee"
          @model={{@session.user}}
          class="flex-shrink-0 text-decoration-none"
          aria-label={{coacheeDisplayName @coachee @session.user}}
        >
          <span
            class="ll-avatar-md rounded-circle d-inline-flex align-items-center justify-content-center fw-bold fs-4
              {{if (isPast @session) 'text-bg-secondary' 'text-bg-primary'}}"
            aria-hidden="true"
          >{{coacheeInitials @coachee @session.user}}</span>
        </LinkTo>

        <div class="flex-grow-1 min-w-0 d-flex flex-column gap-3">
          <div
            class="d-flex flex-column flex-xl-row justify-content-between align-items-start align-items-xl-center gap-3"
          >
            <div class="min-w-0">
              <h3
                class="h5 fw-bold mb-1 text-capitalize"
              >{{@session.sessionType}}</h3>
              <p class="text-body-secondary mb-2">
                <LinkTo
                  @route="coaching.coach.coachees.coachee"
                  @model={{@session.user}}
                  class="link-body-emphasis link-underline-opacity-0 link-underline-opacity-100-hover"
                >{{coacheeDisplayName @coachee @session.user}}</LinkTo>
              </p>
              <div
                class="d-flex flex-wrap align-items-center column-gap-3 row-gap-1 small text-body-secondary fw-semibold"
              >
                <span>{{formatDate (toDate @session.startTime)}}</span>
                <span>{{formatTimeRange
                    (toDate @session.startTime)
                    (toDate @session.endTime)
                  }}</span>
                {{#let
                  (getSessionStatusDisplay @session.status)
                  as |statusDisplay|
                }}
                  <span
                    class="badge rounded-pill
                      {{statusBadgeClass statusDisplay.color}}
                      fw-semibold px-2 py-1 d-inline-flex align-items-center gap-1 text-nowrap"
                    title={{statusDisplay.description}}
                    data-test-session-status-badge
                  >
                    <iconify-icon
                      inline
                      icon={{statusDisplay.icon}}
                      aria-hidden="true"
                    />
                    {{statusDisplay.label}}
                    <span class="visually-hidden">:
                      {{statusDisplay.description}}</span>
                  </span>
                {{/let}}
              </div>
            </div>

            <div
              class="d-flex align-items-center gap-2 flex-shrink-0 flex-wrap"
            >
              <SessionActions
                @session={{@session}}
                @coach={{@coach}}
                @coachingConfig={{@coachingConfig}}
                @buttonClass="btn-sm"
                @onChanged={{@onChanged}}
              />
            </div>
          </div>
        </div>
      </div>

      {{! `session.notes` is the coachee's booking note, not a session note - it is
          embedded in the calendar invite, so it is labelled distinctly to avoid
          being confused with the private note below. }}
      {{#if @session.notes}}
        <div class="rounded-3 p-3 bg-body mt-3">
          <p class="mb-0 small text-body">
            <span class="fw-bold">Booking note from coachee</span>
            ·
            {{@session.notes}}
          </p>
        </div>
      {{/if}}

      <SessionNotes @session={{@session}} @role="coach" class="mt-3" />
    </div>
  </div>
</template>;

export default SessionCard;
